
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getTokenStageRole } from '../../base/jwt/functions';
import {
    isParticipantModerator
} from '../../base/participants';
import { getIsParticipantAudioMuted, getIsParticipantVideoMuted } from '../../base/tracks';
import { ACTION_TRIGGER } from '../constants';
import { getParticipantAudioMediaState, getParticipantVideoMediaState } from '../functions';

import { ParticipantItem } from './ParticipantItem';

type Props = {

    /**
     * Is this item highlighted.
     */
    isHighlighted: boolean,

    /**
     * Callback used to open a confirmation dialog for audio muting.
     */
    muteAudio: Function,

    /**
     * Callback for the activation of this item's context menu.
     */
    onContextMenu: Function,

    /**
     * Callback for the mouse leaving this item.
     */
    onLeave: Function,

    /**
     * Participant reference.
     */
    participant: Object,

    /**
     * True when this row is one of the extra devices of a person, shown under
     * the person's own row.
     */
    isSessionRow?: boolean,

    /**
     * Shows and toggles the person's other devices. Only moderators get it,
     * because only they can act on an individual device.
     */
    onToggleSessions?: Function,

    /**
     * How many endpoints the person behind this row has.
     */
    sessionCount?: number,

    /**
     * Which of the person's devices this row is, 1 based.
     */
    sessionOrdinal?: number,

    /**
     * Whether the person's devices are currently listed.
     */
    sessionsExpanded?: boolean
};

export const MeetingParticipantItem = ({
    isHighlighted,
    onContextMenu,
    onLeave,
    raiseHandP,
    participantList,
    muteAudio,
    participant,
    index,
    hideOptions,
    isSessionRow,
    onToggleSessions,
    sessionCount,
    sessionOrdinal,
    sessionsExpanded
}: Props) => {
    const { t } = useTranslation();
    const isAudioMuted = useSelector(getIsParticipantAudioMuted(participant));
    const isVideoMuted = useSelector(getIsParticipantVideoMuted(participant));
    const audioMediaState = useSelector(getParticipantAudioMediaState(participant, isAudioMuted));
    const videoMediaState = useSelector(getParticipantVideoMediaState(participant, isVideoMuted));

    // A client is never told its own presence properties, so the owner badge
    // for the local row comes from the token claim the server stamped it from.
    const localIsHost = useSelector(state => getTokenStageRole(state) === 'host');
    const isHost = participant?.local ? localIsHost : participant?.webinarHost === true;

    return (
        <ParticipantItem
            actionsTrigger = { ACTION_TRIGGER.HOVER }
            ariaLabel = { t('toolbar.moreOptions') }
            audioMediaState = { audioMediaState }
            hideOptions = { hideOptions }
            index = { index }
            isHidden = { participant?.webinarHidden === true }
            isHighlighted = { isHighlighted }
            isHost = { isHost }
            isModerator = { isParticipantModerator(participant) }
            isSessionRow = { isSessionRow }
            muteAudio = { muteAudio }
            onContextMenu = { onContextMenu }
            onLeave = { onLeave }
            onToggleSessions = { onToggleSessions }
            participant = { participant }
            participantList = { participantList }
            raiseHandP = { raiseHandP }
            sessionCount = { sessionCount }
            sessionOrdinal = { sessionOrdinal }
            sessionsExpanded = { sessionsExpanded }
            videoMuteState = { videoMediaState }>
            {/* {!isInBreakoutRoom && <ParticipantQuickAction
                isAudioMuted={isAudioMuted}
                muteAudio={muteAudio}
                participant={participant}
                participantID={_participantID} />} */}
            {/* <ParticipantActionEllipsis
                aria-label={t('MeetingParticipantItem.ParticipantActionEllipsis.options')}
                onClick={onContextMenu} /> */}
        </ParticipantItem>
    );
};

export const MemoizedMeetingParticipantItem = memo(MeetingParticipantItem);
